/* eslint-disable react/prop-types */
import React, {useState, useEffect, useLayoutEffect} from 'react';
import {useMediaQuery} from 'react-responsive';
import {Link} from 'react-router-dom';
import {Button, Fab, Drawer, Chip} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import MenuIcon from '@mui/icons-material/Menu';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import ForumIcon from '@mui/icons-material/Forum';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import DescriptionIcon from '@mui/icons-material/Description';
import GroupsIcon from '@mui/icons-material/Groups';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import FavoriteIcon from '@mui/icons-material/Favorite';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import styled, {keyframes} from 'styled-components';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Filler,
  Tooltip,
} from 'chart.js';
import {Line, Doughnut} from 'react-chartjs-2';
import Tab from '../../Components/tab';
import Footer from '../../Components/Footer';
import logo from '../../Assets/logo.png';
import fotoCarro from '../../Assets/historiaPrimeiroCarro.png';

// mesma lib (chart.js + react-chartjs-2) usada nos gráficos reais do
// produto (ver EvolucaoEntradasSaidas.js/HistoricoCategoriaChart.js em
// enterpriseireit) — pedido explícito do usuário: mostrar gráfico de
// verdade na landing, não só ícone+texto, "pra mostrar que a gente
// realmente faz muito isso".
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Filler,
  Tooltip,
);

/**
 * Landing page pública do Fiinc (ireit.com.br/financas) — apresentação do
 * módulo Finanças (produto) e do agente Fiinc (o assistente de IA dentro
 * dele), com CTA pro cadastro que já existe em /cadastro_financas.
 *
 * Conteúdo/copy vem de docs/FIINC_LANDING_COPY.md no repo `enterpriseireit`
 * (não aberto aqui) — não inventar feature nova sem checar lá primeiro.
 * Estrutura de página (Tab/Drawer no mobile, Footer, Fab de voltar ao topo)
 * espelha `src/Pages/main_page/index.js`, o padrão já estabelecido no site.
 *
 * Planos (`const PLANOS` abaixo) são hardcoded com valor real, passado pelo
 * usuário em 2026-09-02 — não dá pra buscar de `GET /planos` (o endpoint
 * real, usado em TabAssinatura.js/enterpriseireit) porque ele exige token
 * (confirmado ao vivo: 511 "No Authorization header" sem auth), e a landing
 * é pública/pré-login. Mesmo padrão de `register_gestor/planos/*.json` pro
 * mesmo problema. Se o preço mudar no backend, tem que atualizar aqui à mão.
 */

const fadeUp = keyframes`
  from { transform: translateY(28px); opacity: 0; }
  to   { transform: translateY(0);    opacity: 1; }
`;
const FadeUp = styled.div`
  animation: ${fadeUp} 0.6s ease-out both;
  animation-delay: ${p => p.delay || '0s'};
`;

const C = {
  azul: '#06448e',
  azulD: '#04316a',
  azulClaro: '#096be0',
  laranja: '#FF6600',
  laranjaD: '#cc4400',
  roxo: '#7c53e3',
  verde: '#059669',
  bg: '#f4f6fa',
  text: '#1a1a2e',
  muted: '#6b7280',
  white: '#ffffff',
};

const NAV_HEIGHT = 60;

const Section = ({children, bg, id, style, maxWidth}) => (
  <div
    id={id}
    style={{
      width: '100%',
      background: bg || C.white,
      display: 'flex',
      justifyContent: 'center',
      boxSizing: 'border-box',
      ...style,
    }}
  >
    <div
      style={{width: '100%', maxWidth: maxWidth || 1100, padding: '64px 20px'}}
    >
      {children}
    </div>
  </div>
);

const Eyebrow = ({children, cor}) => (
  <p
    style={{
      color: cor || C.laranja,
      fontWeight: 800,
      fontSize: 13,
      letterSpacing: 1,
      textTransform: 'uppercase',
      margin: '0 0 8px',
    }}
  >
    {children}
  </p>
);

const SectionTitle = ({children, center}) => (
  <h2
    style={{
      color: C.text,
      fontSize: 32,
      fontWeight: 800,
      margin: '0 0 14px',
      textAlign: center ? 'center' : 'left',
      lineHeight: 1.2,
    }}
  >
    {children}
  </h2>
);

const SectionLead = ({children, center}) => (
  <p
    style={{
      color: C.muted,
      fontSize: 16,
      lineHeight: 1.6,
      maxWidth: 620,
      margin: center ? '0 auto 40px' : '0 0 40px',
      textAlign: center ? 'center' : 'left',
    }}
  >
    {children}
  </p>
);

const CTAButton = ({children, to, outline, ...rest}) => (
  <Button
    component={Link}
    to={to}
    variant={outline ? 'outlined' : 'contained'}
    endIcon={<ArrowForwardIcon />}
    style={{
      textTransform: 'none',
      fontWeight: 700,
      fontSize: 15,
      padding: '12px 26px',
      borderRadius: 12,
    }}
    sx={
      outline
        ? {
            color: C.white,
            borderColor: C.white,
            ':hover': {bgcolor: C.white, color: C.azul, borderColor: C.white},
          }
        : {
            bgcolor: C.laranja,
            color: C.white,
            boxShadow: '0 8px 24px rgba(255,102,0,0.35)',
            ':hover': {bgcolor: C.laranjaD},
          }
    }
    {...rest}
  >
    {children}
  </Button>
);

// ─── Mockup do produto pro hero (sem imagem real — ainda não temos
// screenshot oficial pra usar aqui) ─────────────────────────────────────────
const AppMockup = () => (
  <div
    style={{
      background: C.white,
      borderRadius: 20,
      boxShadow: '0 24px 64px rgba(6,68,142,0.28)',
      width: '100%',
      maxWidth: 380,
      overflow: 'hidden',
    }}
  >
    <div
      style={{
        background: C.azul,
        padding: '12px 16px',
        display: 'flex',
        alignItems: 'center',
        gap: 6,
      }}
    >
      {['#ff5f57', '#febc2e', '#28c840'].map(cor => (
        <span
          key={cor}
          style={{
            width: 9,
            height: 9,
            borderRadius: '50%',
            background: cor,
            display: 'inline-block',
          }}
        />
      ))}
      <span style={{color: '#dbe6f5', fontSize: 12, marginLeft: 8}}>
        Fechamento de agosto
      </span>
    </div>
    <div style={{padding: 18}}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          background: '#e9f6ee',
          borderRadius: 12,
          padding: '10px 12px',
          marginBottom: 14,
        }}
      >
        <CheckCircleIcon style={{color: C.verde, fontSize: 20}} />
        <span style={{fontSize: 13, color: C.text, fontWeight: 600}}>
          Fatura Nubank.pdf importada e classificada
        </span>
      </div>

      {[
        {label: 'Moradia', pct: 34, cor: C.azul},
        {label: 'Alimentação', pct: 24, cor: C.laranja},
        {label: 'Transporte', pct: 16, cor: C.roxo},
      ].map(row => (
        <div key={row.label} style={{marginBottom: 10}}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: 12,
              color: C.muted,
              marginBottom: 4,
            }}
          >
            <span>{row.label}</span>
            <span>{row.pct}%</span>
          </div>
          <div
            style={{
              height: 6,
              borderRadius: 4,
              background: '#eef1f6',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                width: `${row.pct}%`,
                height: '100%',
                background: row.cor,
                borderRadius: 4,
              }}
            />
          </div>
        </div>
      ))}

      <div
        style={{
          marginTop: 16,
          background: C.bg,
          borderRadius: 12,
          padding: 12,
          display: 'flex',
          gap: 10,
          alignItems: 'flex-start',
        }}
      >
        <div
          style={{
            width: 26,
            height: 26,
            borderRadius: '50%',
            background: C.azul,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <AutoAwesomeIcon style={{color: '#fff', fontSize: 14}} />
        </div>
        <p style={{fontSize: 12.5, color: C.text, margin: 0, lineHeight: 1.5}}>
          <b>Fiinc:</b> vocês estão no perfil{' '}
          <b style={{color: C.verde}}>&quot;No Caminho Certo&quot;</b> — a sobra
          do mês vem crescendo 3 meses seguidos. Quer que eu ajuste a meta de
          Alimentação pro mês que vem?
        </p>
      </div>
    </div>
  </div>
);

const visualCardStyle = {
  background: C.white,
  borderRadius: 18,
  boxShadow: '0 16px 44px rgba(20,30,60,0.12)',
  padding: 20,
  width: '100%',
  maxWidth: 340,
};

const LegendDot = ({cor, label}) => (
  <div style={{display: 'flex', alignItems: 'center', gap: 6}}>
    <span
      style={{
        width: 8,
        height: 8,
        borderRadius: '50%',
        background: cor,
        display: 'inline-block',
        flexShrink: 0,
      }}
    />
    <span style={{fontSize: 11, color: C.muted}}>{label}</span>
  </div>
);

// mesmo racional do AppMockup do hero — sem screenshot real do produto
// ainda, mas usando a MESMA lib de gráfico (chart.js) que
// EvolucaoEntradasSaidas.js/HistoricoCategoriaChart.js usam de verdade em
// enterpriseireit, com dado ilustrativo fixo.
const VisualSaude = () => (
  <div style={visualCardStyle}>
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 12,
      }}
    >
      <span style={{fontSize: 12, fontWeight: 700, color: C.muted}}>
        Entradas × Saídas
      </span>
      <Chip
        label="No Caminho Certo"
        size="small"
        style={{
          background: '#e6f4ea',
          color: C.verde,
          fontWeight: 700,
          fontSize: 10,
          height: 20,
        }}
      />
    </div>
    <div style={{height: 140}}>
      <Line
        data={{
          labels: ['Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago'],
          datasets: [
            {
              label: 'Entradas',
              data: [4800, 4800, 4900, 4850, 5000, 5050],
              borderColor: C.verde,
              backgroundColor: `${C.verde}22`,
              fill: true,
              tension: 0.35,
              pointRadius: 0,
              borderWidth: 2,
            },
            {
              label: 'Saídas',
              data: [4600, 4500, 4300, 4150, 4000, 3900],
              borderColor: C.laranja,
              backgroundColor: 'transparent',
              tension: 0.35,
              pointRadius: 0,
              borderWidth: 2,
            },
          ],
        }}
        options={{
          responsive: true,
          maintainAspectRatio: false,
          plugins: {legend: {display: false}, tooltip: {enabled: false}},
          scales: {
            x: {
              grid: {display: false},
              ticks: {font: {size: 10}, color: C.muted},
            },
            y: {display: false},
          },
        }}
      />
    </div>
    <div style={{display: 'flex', gap: 16, marginTop: 10}}>
      <LegendDot cor={C.verde} label="Entradas" />
      <LegendDot cor={C.laranja} label="Saídas" />
    </div>
  </div>
);

const VisualRelatorio = () => (
  <div style={visualCardStyle}>
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 14,
      }}
    >
      <span style={{fontSize: 12, fontWeight: 700, color: C.muted}}>
        Categorias — Agosto
      </span>
      <DescriptionIcon style={{color: C.azul, fontSize: 18}} />
    </div>
    <div style={{display: 'flex', alignItems: 'center', gap: 18}}>
      <div style={{width: 108, height: 108, flexShrink: 0}}>
        <Doughnut
          data={{
            labels: ['Moradia', 'Alimentação', 'Transporte', 'Outros'],
            datasets: [
              {
                data: [38, 24, 16, 22],
                backgroundColor: [C.azul, C.laranja, C.roxo, '#cbd5e1'],
                borderWidth: 0,
              },
            ],
          }}
          options={{
            plugins: {legend: {display: false}, tooltip: {enabled: false}},
            cutout: '70%',
          }}
        />
      </div>
      <div style={{display: 'flex', flexDirection: 'column', gap: 8}}>
        <LegendDot cor={C.azul} label="Moradia — 38%" />
        <LegendDot cor={C.laranja} label="Alimentação — 24%" />
        <LegendDot cor={C.roxo} label="Transporte — 16%" />
        <LegendDot cor="#cbd5e1" label="Outros — 22%" />
      </div>
    </div>
  </div>
);

const VisualFechamento = () => (
  <div style={visualCardStyle}>
    <span style={{fontSize: 12, fontWeight: 700, color: C.muted}}>
      Fechamento de agosto
    </span>
    <div
      style={{marginTop: 14, display: 'flex', flexDirection: 'column', gap: 12}}
    >
      {[
        {l: 'Valores do mês', done: true},
        {l: 'Categorias revisadas', done: true},
        {l: 'Reserva de emergência', done: true},
        {l: 'Metas do mês seguinte', done: false},
      ].map(step => (
        <div
          key={step.l}
          style={{display: 'flex', alignItems: 'center', gap: 10}}
        >
          {step.done ? (
            <CheckCircleIcon
              style={{color: C.verde, fontSize: 19, flexShrink: 0}}
            />
          ) : (
            <div
              style={{
                width: 17,
                height: 17,
                borderRadius: '50%',
                border: `2px solid ${C.laranja}`,
                flexShrink: 0,
              }}
            />
          )}
          <span
            style={{
              fontSize: 13,
              color: step.done ? C.text : C.laranja,
              fontWeight: step.done ? 500 : 700,
            }}
          >
            {step.l}
          </span>
        </div>
      ))}
    </div>
  </div>
);

const VisualFiinc = () => (
  <div style={visualCardStyle}>
    <div
      style={{display: 'flex', justifyContent: 'flex-end', marginBottom: 10}}
    >
      <div
        style={{
          background: C.azul,
          color: '#fff',
          borderRadius: '14px 14px 2px 14px',
          padding: '9px 13px',
          fontSize: 12.5,
          maxWidth: '85%',
        }}
      >
        Vale a pena comprar um iPhone parcelado em 6x?
      </div>
    </div>
    <div style={{display: 'flex', gap: 8, alignItems: 'flex-start'}}>
      <div
        style={{
          width: 24,
          height: 24,
          borderRadius: '50%',
          background: C.laranja,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <AutoAwesomeIcon style={{color: '#fff', fontSize: 13}} />
      </div>
      <div
        style={{
          background: C.bg,
          borderRadius: '2px 14px 14px 14px',
          padding: '9px 13px',
          fontSize: 12.5,
          color: C.text,
          lineHeight: 1.5,
        }}
      >
        Dá pra parcelar sem estourar a meta — mas sobra menos folga. Quer que eu
        separe <b>R$ 350/mês</b> numa caixinha em vez disso?
      </div>
    </div>
  </div>
);

const DIFERENCIAIS = [
  {
    icon: MonitorHeartIcon,
    cor: C.roxo,
    titulo: 'Saúde Financeira: um diagnóstico, não só um gráfico',
    texto:
      'A cada mês, o Fiinc olha 12 meses de histórico real e dá um nome pro momento da família — de "No Caminho Certo" a "No Vermelho" — com o próximo passo pra evoluir. Como um exame de sangue: o que importa não é um mês isolado, é o formato da linha ao longo do tempo.',
    Visual: VisualSaude,
  },
  {
    icon: DescriptionIcon,
    cor: C.azul,
    titulo: 'Um relatório mensal como se fosse feito por um consultor',
    texto:
      'Ao fechar o mês, vocês recebem um relatório completo em PDF — com laudo escrito por IA, categorias, dívidas, reserva e até uma projeção de independência financeira. Dá pra imprimir, guardar, ou só mostrar pro parceiro no fim do mês.',
    Visual: VisualRelatorio,
  },
  {
    icon: EventAvailableIcon,
    cor: C.laranja,
    titulo: 'Um ritual de fechamento, não uma tela passiva',
    texto:
      'Nada de só olhar o saldo e torcer. Um passo a passo curto revisa o mês, compara com a meta que vocês definiram no mês anterior, resolve pendências e já deixa tudo pronto pro próximo — sozinho ou a dois.',
    Visual: VisualFechamento,
  },
  {
    icon: ForumIcon,
    cor: C.verde,
    titulo: 'Fiinc: converse, e ele resolve — não só responde',
    texto:
      '"Vale a pena comprar um iPhone parcelado em 6x?" O Fiinc responde com os dados reais da família, mostra gráfico quando ajuda, e pode agir direto na conversa: ajustar uma meta, registrar um pagamento, criar uma caixinha.',
    Visual: VisualFiinc,
  },
];

const PASSOS = [
  {
    icon: CloudUploadIcon,
    titulo: 'Importe',
    texto:
      'Solte o CSV do extrato (Nubank, Flash, Bradesco) ou a fatura em PDF do cartão. Pode misturar formatos e soltar vários arquivos de uma vez.',
  },
  {
    icon: AutoAwesomeIcon,
    titulo: 'Deixe a IA organizar',
    texto:
      'Cada transação é classificada sozinha. Fatura em PDF passa por uma revisão rápida antes de valer de verdade — nada entra sem você conferir.',
  },
  {
    icon: EventAvailableIcon,
    titulo: 'Feche o mês guiado',
    texto:
      'Um passo a passo curto revisa valores, categorias e objetivos, e compara com o que vocês planejaram no mês anterior.',
  },
  {
    icon: ForumIcon,
    titulo: 'Converse com o Fiinc',
    texto:
      'Pergunte qualquer coisa sobre o dinheiro da família e receba uma resposta com dado real — e ação, quando fizer sentido.',
  },
];

// Seção de privacidade (2026-09-03, pedido explícito do usuário): alguns
// usuários perguntaram se ele fica olhando as contas da família — ele não
// tem interesse nisso e quer deixar isso explícito antes da seção de
// planos, junto com a possibilidade de excluir os dados quando quiser.
const PRIVACIDADE = [
  {
    icon: VisibilityOffOutlinedIcon,
    cor: C.azul,
    titulo: 'Seus dados não passam pela gente',
    texto:
      'O time do Irê it não acompanha, não analisa e não tem motivo pra abrir os dados financeiros de nenhuma família. Quem lê seu extrato é a IA, de forma automática — não uma pessoa.',
  },
  {
    icon: DeleteOutlineIcon,
    cor: C.laranja,
    titulo: 'Apague quando quiser',
    texto:
      'Você pode excluir sua conta e todos os seus dados a qualquer momento, direto pelo app — sem precisar pedir permissão nem explicar o motivo.',
  },
  {
    icon: LockOutlinedIcon,
    cor: C.roxo,
    titulo: 'Só a sua família vê o que é da sua família',
    texto:
      'Cada grupo familiar enxerga só os próprios dados. Ninguém de fora do grupo — nem a gente — tem acesso ao que vocês registram.',
  },
];

// Papéis reais de grupo (ver PAPEIS_GRUPO em Financas/index.js,
// enterpriseireit: dono/membro/crianca) — pedido explícito do usuário: dar
// pra ver que o Fiinc trata como grupo FAMILIAR (com filhos incluídos), não
// só um app pra 1-2 adultos dividirem conta.
const FAMILIA_MEMBROS = [
  {nome: 'Você', cor: C.azul, papel: 'Dono do grupo', gasto: 2350},
  {nome: 'Seu par', cor: C.laranja, papel: 'Membro', gasto: 1680},
  {nome: 'Seu filho(a)', cor: C.roxo, papel: 'Criança', gasto: 320},
];

// Espelha os planos reais de TabAssinatura.js (enterpriseireit, PLANO_CFG +
// LinhaBeneficio) — hardcoded aqui de propósito: GET /planos exige token
// (confirmado ao vivo: 511 "No Authorization header" sem auth), então não
// dá pra buscar isso da landing pública. Mesmo padrão já usado em
// register_gestor/planos/*.json pro mesmo problema (mostrar preço
// pré-login). Valores passados pelo usuário em 2026-09-02 — se o preço
// mudar no backend, precisa atualizar aqui também à mão. Bullet de cota
// de importação do 1º mês (2026-09-03) espelha
// PlanoFinanceiro.limites.transacoesImportacaoPrimeiroMes
// (financas_modules/scripts/seed-planos.js) — mesmo cuidado de
// sincronizar à mão.
// benefício simples = sempre ativo (texto puro); {texto, ativo:false} = mostra
// riscado/cinza — usado no OpenFinance dos planos Básico, que não tem (só o
// Free e os Prime têm de verdade, confirmado pelo usuário em 2026-09-02).
const openFinanceAtivo = {texto: 'Integração OpenFinance', ativo: true};
const openFinanceInativo = {texto: 'Integração OpenFinance', ativo: false};

const PLANOS = [
  {
    nome: 'Free',
    cor: '#6b7280',
    preco: 'R$ 0,00',
    membros: 1,
    destaque: false,
    cta: 'Plano gratuito',
    beneficios: [
      '300 transações no total, pra você testar',
      '1 laudo de IA no total, pra você testar',
      '2 fechamentos de mês',
      'Classificação automática por IA',
      openFinanceInativo,
      'Lembretes via WhatsApp',
    ],
  },
  {
    nome: 'Individual Básico',
    cor: '#3b82f6',
    preco: 'R$ 19,90',
    membros: 1,
    destaque: false,
    cta: 'Assinar',
    beneficios: [
      '400 transações por mês',
      'Até 3.000 transações liberadas no 1º mês, pra trazer seu histórico',
      '3 laudos de IA por mês',
      'Fechamentos de mês ilimitados',
      'Classificação automática por IA',
      openFinanceInativo,
      'Lembretes via WhatsApp',
    ],
  },
  {
    nome: 'Individual Prime',
    cor: '#1d4ed8',
    preco: 'R$ 29,90',
    membros: 1,
    destaque: true,
    cta: 'Assinar',
    beneficios: [
      '600 transações por mês',
      'Até 4.000 transações liberadas no 1º mês, pra trazer seu histórico',
      '5 laudos de IA por mês',
      'Fechamentos de mês ilimitados',
      'Classificação automática por IA',
      openFinanceAtivo,
      'Lembretes via WhatsApp',
    ],
  },
  {
    nome: 'Casal Básico',
    cor: '#10b981',
    preco: 'R$ 39,90',
    membros: 2,
    destaque: false,
    cta: 'Assinar',
    beneficios: [
      '1.000 transações por mês',
      'Até 6.000 transações liberadas no 1º mês, pra trazer seu histórico',
      '5 laudos de IA por mês',
      'Fechamentos de mês ilimitados',
      'Classificação automática por IA',
      openFinanceInativo,
      'Lembretes via WhatsApp',
    ],
  },
  {
    nome: 'Casal Prime',
    cor: '#047857',
    preco: 'R$ 49,90',
    membros: 2,
    destaque: true,
    cta: 'Assinar',
    beneficios: [
      '2.000 transações por mês',
      'Até 8.000 transações liberadas no 1º mês, pra trazer seu histórico',
      '5 laudos de IA por mês',
      'Fechamentos de mês ilimitados',
      'Classificação automática por IA',
      openFinanceAtivo,
      'Lembretes via WhatsApp',
    ],
  },
  {
    nome: 'Família Básico',
    cor: '#a855f7',
    preco: 'R$ 89,90',
    membros: 5,
    destaque: false,
    cta: 'Assinar',
    beneficios: [
      '2.800 transações por mês',
      'Até 10.000 transações liberadas no 1º mês, pra trazer seu histórico',
      '11 laudos de IA por mês',
      'Fechamentos de mês ilimitados',
      'Classificação automática por IA',
      openFinanceInativo,
      'Lembretes via WhatsApp',
    ],
  },
  {
    nome: 'Família Prime',
    cor: '#7e22ce',
    preco: 'R$ 114,90',
    membros: 5,
    destaque: true,
    cta: 'Assinar',
    beneficios: [
      '6.200 transações por mês',
      'Até 12.000 transações liberadas no 1º mês, pra trazer seu histórico',
      '11 laudos de IA por mês',
      'Fechamentos de mês ilimitados',
      'Classificação automática por IA',
      openFinanceAtivo,
      'Lembretes via WhatsApp',
    ],
  },
];

const FinancasLanding = () => {
  const isSmall = useMediaQuery({query: '(max-width: 750.98px)'});
  const isVerySmall = useMediaQuery({query: '(max-width:450.00px)'});
  const [toggleBar, setToggleBar] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const alvo = window.location.hash
      ? document.getElementById(window.location.hash.slice(1))
      : null;
    if (alvo) {
      alvo.scrollIntoView({behavior: 'smooth'});
    } else {
      window.scrollTo({top: 0, behavior: 'smooth'});
    }
  }, []);

  useEffect(() => {
    const t = () => setIsVisible(window.pageYOffset > 50);
    window.addEventListener('scroll', t);
    return () => window.removeEventListener('scroll', t);
  }, []);

  function useWindowSize() {
    const [size, setSize] = useState([0, 0]);
    useLayoutEffect(() => {
      function updateSize() {
        setSize([window.innerWidth, window.innerHeight]);
      }
      window.addEventListener('resize', updateSize);
      updateSize();
      return () => window.removeEventListener('resize', updateSize);
    }, []);
    return size;
  }
  // eslint-disable-next-line no-unused-vars
  const [width] = useWindowSize();

  const mobile = isSmall || isVerySmall;

  return (
    <div style={{overflowX: 'hidden', width: '100%', background: C.white}}>
      {isVisible && (
        <Fab
          onClick={() => window.scrollTo({top: 0, behavior: 'smooth'})}
          style={{position: 'fixed', bottom: 20, left: 20, zIndex: 900}}
          size="small"
          color="primary"
          aria-label="voltar ao topo"
        >
          <ArrowUpwardIcon />
        </Fab>
      )}

      {/* ─── Barra de navegação (mesmo padrão de main_page/index.js) ────── */}
      {mobile ? (
        <div
          style={{
            background: C.azul,
            height: NAV_HEIGHT,
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'space-between',
            position: 'fixed',
            zIndex: 999,
            top: 0,
            left: 0,
            width: '100%',
          }}
        >
          <Button
            component={Link}
            to="/"
            style={{
              width: 95,
              height: 47,
              marginLeft: 12,
              backgroundImage: `url(${logo})`,
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              backgroundSize: 'cover',
            }}
          />
          <div style={{display: 'flex', alignItems: 'center', gap: 10}}>
            <Button
              component="a"
              href="https://myireit.com.br"
              variant="outlined"
              size="small"
              style={{
                textTransform: 'none',
                fontWeight: 700,
                fontSize: 12.5,
                borderRadius: 999,
                padding: '4px 12px',
              }}
              sx={{
                color: '#ffffff',
                borderColor: 'rgba(255,255,255,0.6)',
                ':hover': {
                  borderColor: '#ffffff',
                  bgcolor: 'rgba(255,255,255,0.08)',
                },
              }}
            >
              Acessar painel
            </Button>
            <MenuIcon
              style={{color: '#ffffff', marginRight: 15}}
              onClick={() => setToggleBar(true)}
            />
          </div>
        </div>
      ) : (
        <div style={{position: 'relative'}}>
          <Tab />
          <Button
            component="a"
            href="https://myireit.com.br"
            variant="contained"
            size="small"
            style={{
              position: 'fixed',
              top: 12,
              right: 20,
              zIndex: 1000,
              textTransform: 'none',
              fontWeight: 700,
              fontSize: 13,
              borderRadius: 999,
              padding: '6px 16px',
            }}
            sx={{bgcolor: C.laranja, ':hover': {bgcolor: C.laranjaD}}}
          >
            Acessar painel
          </Button>
        </div>
      )}
      {mobile && (
        <Drawer
          anchor="top"
          open={toggleBar}
          onClose={() => setToggleBar(false)}
        >
          <Tab setToggleBar={setToggleBar} />
        </Drawer>
      )}

      {/* ─── Hero ────────────────────────────────────────────────────────── */}
      <Section
        bg={`linear-gradient(160deg, ${C.azul} 0%, ${C.azulD} 100%)`}
        style={{paddingTop: NAV_HEIGHT}}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: mobile ? 'column' : 'row',
            alignItems: 'center',
            gap: 40,
            paddingTop: 40,
          }}
        >
          <FadeUp style={{flex: 1, minWidth: 0}}>
            <Chip
              label="Irê it Finanças · Fiinc"
              style={{
                background: 'rgba(255,255,255,0.12)',
                color: '#dbe6f5',
                fontWeight: 700,
                marginBottom: 18,
              }}
            />
            <h1
              style={{
                color: C.white,
                fontSize: mobile ? 32 : 44,
                fontWeight: 800,
                lineHeight: 1.15,
                margin: '0 0 18px',
              }}
            >
              Pare de viver pra pagar conta. Comece a entender pra onde o
              dinheiro vai.
            </h1>
            <p
              style={{
                color: '#dbe6f5',
                fontSize: 17,
                lineHeight: 1.6,
                marginBottom: 30,
                maxWidth: 480,
              }}
            >
              Importe seus extratos, deixe a IA organizar, siga um fechamento de
              mês guiado e converse com o <b>Fiinc</b> sempre que precisar de
              uma resposta rápida sobre o dinheiro da família.
            </p>
            <div
              style={{
                display: 'flex',
                gap: 14,
                flexWrap: 'wrap',
              }}
            >
              <CTAButton to="/cadastro_financas">
                Criar minha conta grátis
              </CTAButton>
              <CTAButton to="/financas/demo" outline>
                Ver como funciona
              </CTAButton>
            </div>
          </FadeUp>
          <FadeUp
            delay="0.15s"
            style={{
              flex: mobile ? 'unset' : 1,
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <AppMockup />
          </FadeUp>
        </div>
      </Section>

      {/* ─── A dor / a origem ────────────────────────────────────────────── */}
      <Section bg={C.bg}>
        <div
          style={{
            display: 'flex',
            flexDirection: mobile ? 'column' : 'row',
            alignItems: 'center',
            gap: mobile ? 24 : 48,
          }}
        >
          <div
            style={{
              flex: mobile ? 'unset' : '0 0 260px',
              width: mobile ? 200 : 260,
            }}
          >
            <img
              src={fotoCarro}
              alt="João e sua esposa no dia que compraram o primeiro carro"
              style={{
                width: '100%',
                display: 'block',
                borderRadius: 20,
                boxShadow: '0 20px 48px rgba(20,30,60,0.18)',
              }}
            />
          </div>
          <div style={{flex: 1}}>
            <Eyebrow>Por que o Fiinc existe</Eyebrow>
            <SectionTitle>
              &quot;Estávamos vivendo pra pagar contas, sem enxergar o quadro
              todo.&quot;
            </SectionTitle>
            <p
              style={{
                color: C.muted,
                fontSize: 16,
                lineHeight: 1.7,
                marginBottom: 8,
              }}
            >
              Depois de comprar nosso primeiro carro, achamos que o aperto ia
              passar. Não passou — dois meses depois, as contas estavam mais
              difíceis de fechar do que antes, e a gente não sabia dizer por
              quê. Foi aí que nasceu o Fiinc: construímos uma ferramenta pra
              colocar as finanças da família na ponta do lápis de verdade,
              entender cada gasto e organizar as dívidas de uma vez por todas.
              Hoje ele está disponível pra qualquer família passando pelo mesmo
              aperto que a gente passou.
            </p>
            <Button
              component={Link}
              to="/financas/historia"
              endIcon={<ArrowForwardIcon />}
              style={{textTransform: 'none', fontWeight: 700, fontSize: 14}}
              sx={{color: C.azul}}
            >
              Leia nossa história completa
            </Button>
          </div>
        </div>
      </Section>

      {/* ─── Como funciona ───────────────────────────────────────────────── */}
      <Section id="como-funciona">
        <Eyebrow>Como funciona</Eyebrow>
        <SectionTitle>
          Do extrato bagunçado ao seu primeiro objetivo.
        </SectionTitle>
        <SectionLead>
          Quatro passos simples, todo mês — sem planilha, sem adivinhação.
        </SectionLead>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: mobile ? '1fr' : 'repeat(4, 1fr)',
            gap: 24,
          }}
        >
          {PASSOS.map((p, i) => (
            <div key={p.titulo}>
              <div
                style={{
                  width: 46,
                  height: 46,
                  borderRadius: 12,
                  background: C.bg,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: 14,
                  position: 'relative',
                }}
              >
                <p.icon style={{color: C.azul, fontSize: 24}} />
                <span
                  style={{
                    position: 'absolute',
                    top: -8,
                    right: -8,
                    width: 20,
                    height: 20,
                    borderRadius: '50%',
                    background: C.laranja,
                    color: '#fff',
                    fontSize: 11,
                    fontWeight: 800,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {i + 1}
                </span>
              </div>
              <p
                style={{
                  fontWeight: 700,
                  fontSize: 16,
                  color: C.text,
                  margin: '0 0 6px',
                }}
              >
                {p.titulo}
              </p>
              <p
                style={{
                  fontSize: 13.5,
                  color: C.muted,
                  lineHeight: 1.55,
                  margin: 0,
                }}
              >
                {p.texto}
              </p>
            </div>
          ))}
        </div>
        <div style={{textAlign: 'center', marginTop: 40}}>
          <CTAButton to="/financas/demo">
            Testar demonstração interativa
          </CTAButton>
          <p style={{fontSize: 12, color: C.muted, marginTop: 10}}>
            Sem cadastro, sem dado real — só pra você ver como é.
          </p>
        </div>
      </Section>

      {/* ─── Diferenciais ────────────────────────────────────────────────── */}
      <Section bg={C.bg}>
        <Eyebrow>O que nenhum app comum de gastos tem</Eyebrow>
        <SectionTitle>
          Feito pra mudar comportamento, não só mostrar saldo.
        </SectionTitle>
        <SectionLead>
          Cada um desses pontos existe porque resolveu um problema nosso de
          verdade.
        </SectionLead>
        <div style={{display: 'flex', flexDirection: 'column', gap: 56}}>
          {DIFERENCIAIS.map((d, i) => (
            <div
              key={d.titulo}
              style={{
                display: 'flex',
                flexDirection: mobile
                  ? 'column'
                  : i % 2
                  ? 'row-reverse'
                  : 'row',
                alignItems: 'center',
                gap: mobile ? 24 : 44,
              }}
            >
              <div style={{flex: 1, width: '100%'}}>
                <div
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: 12,
                    background: `${d.cor}1a`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: 16,
                  }}
                >
                  <d.icon style={{color: d.cor, fontSize: 24}} />
                </div>
                <p
                  style={{
                    fontWeight: 700,
                    fontSize: 19,
                    color: C.text,
                    margin: '0 0 10px',
                  }}
                >
                  {d.titulo}
                </p>
                <p
                  style={{
                    fontSize: 14.5,
                    color: C.muted,
                    lineHeight: 1.65,
                    margin: 0,
                  }}
                >
                  {d.texto}
                </p>
              </div>
              <div
                style={{
                  flex: 1,
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <d.Visual />
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* ─── Feito pra família ───────────────────────────────────────────── */}
      <Section>
        <div
          style={{
            display: 'flex',
            flexDirection: mobile ? 'column' : 'row',
            alignItems: 'center',
            gap: 40,
          }}
        >
          <div style={{flex: 1}}>
            <div
              style={{
                width: 52,
                height: 52,
                borderRadius: 14,
                background: `${C.roxo}1a`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: 18,
              }}
            >
              <GroupsIcon style={{color: C.roxo, fontSize: 28}} />
            </div>
            <SectionTitle>
              Feito pra família, não só pra uma pessoa.
            </SectionTitle>
            <p
              style={{
                color: C.muted,
                fontSize: 15,
                lineHeight: 1.7,
                maxWidth: 480,
              }}
            >
              Convide quem mora com você — cônjuge, filhos, ou quem mais divide
              as contas. Cada pessoa tem seu papel (dono, membro ou criança),
              sua cor e sua contribuição visível, e o relatório mensal fala com
              cada uma individualmente, além de falar da família como um todo.
            </p>
          </div>
          <div style={{flex: 1, width: '100%'}}>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
                marginBottom: 18,
              }}
            >
              {FAMILIA_MEMBROS.map(m => (
                <div
                  key={m.nome}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    background: C.bg,
                    borderRadius: 12,
                    padding: '11px 16px',
                  }}
                >
                  <div
                    style={{
                      width: 34,
                      height: 34,
                      borderRadius: '50%',
                      background: m.cor,
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 700,
                      fontSize: 14,
                      flexShrink: 0,
                    }}
                  >
                    {m.nome.charAt(0)}
                  </div>
                  <div>
                    <p
                      style={{
                        margin: 0,
                        fontWeight: 700,
                        fontSize: 14,
                        color: C.text,
                      }}
                    >
                      {m.nome}
                    </p>
                    <p style={{margin: 0, fontSize: 12, color: C.muted}}>
                      {m.papel}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* mesmo padrão visual de CategoriaCard.js (porMembro) em
                enterpriseireit: barra segmentada proporcional ao gasto de
                cada um + legenda com bolinha/nome/valor — pedido explícito
                do usuário pra mostrar que tratamos como grupo familiar, não
                só individual */}
            <div
              style={{
                background: C.white,
                borderRadius: 14,
                padding: '14px 16px',
                border: '1px solid #e5e9f0',
              }}
            >
              <p
                style={{
                  margin: '0 0 10px',
                  fontSize: 12,
                  fontWeight: 700,
                  color: C.muted,
                }}
              >
                Gastos de agosto por pessoa
              </p>
              <div
                style={{
                  display: 'flex',
                  gap: 3,
                  height: 10,
                  borderRadius: 6,
                  overflow: 'hidden',
                }}
              >
                {FAMILIA_MEMBROS.map(m => (
                  <div
                    key={m.nome}
                    title={`${m.nome}: R$ ${m.gasto.toLocaleString('pt-BR')}`}
                    style={{flex: m.gasto, background: m.cor}}
                  />
                ))}
              </div>
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 14,
                  marginTop: 10,
                }}
              >
                {FAMILIA_MEMBROS.map(m => (
                  <div
                    key={m.nome}
                    style={{display: 'flex', alignItems: 'center', gap: 5}}
                  >
                    <span
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        background: m.cor,
                        flexShrink: 0,
                      }}
                    />
                    <span style={{fontSize: 11.5, color: C.muted}}>
                      {m.nome}: R$ {m.gasto.toLocaleString('pt-BR')}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* ─── Privacidade ─────────────────────────────────────────────────── */}
      <Section>
        <Eyebrow cor={C.verde}>Privacidade</Eyebrow>
        <SectionTitle>A gente não fica de olho nas suas contas.</SectionTitle>
        <SectionLead>
          Sabemos que dado financeiro é sensível — por isso o Fiinc foi pensado
          pra ser seu, não nosso.
        </SectionLead>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: mobile ? '1fr' : 'repeat(3, 1fr)',
            gap: 24,
          }}
        >
          {PRIVACIDADE.map(p => (
            <div key={p.titulo}>
              <div
                style={{
                  width: 46,
                  height: 46,
                  borderRadius: 12,
                  background: `${p.cor}1a`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: 14,
                }}
              >
                <p.icon style={{color: p.cor, fontSize: 24}} />
              </div>
              <p
                style={{
                  fontWeight: 700,
                  fontSize: 16,
                  color: C.text,
                  margin: '0 0 6px',
                }}
              >
                {p.titulo}
              </p>
              <p
                style={{
                  fontSize: 13.5,
                  color: C.muted,
                  lineHeight: 1.55,
                  margin: 0,
                }}
              >
                {p.texto}
              </p>
            </div>
          ))}
        </div>
      </Section>

      {/* ─── Planos ──────────────────────────────────────────────────────── */}
      <Section bg={C.bg} maxWidth={1280} id="planos">
        <SectionTitle center>Escolha seu plano.</SectionTitle>
        <SectionLead center>
          Ajuste transações, laudos de IA e integrações ao que sua família
          precisa — troque de plano quando quiser.
        </SectionLead>
        <div
          style={{
            display: 'flex',
            flexWrap: 'nowrap',
            overflowX: 'auto',
            gap: 18,
            padding: '4px 4px 14px',
            marginBottom: -14,
            // deixa a última carta respirar no fim do scroll em vez de
            // colar na borda
            scrollSnapType: 'x proximity',
          }}
        >
          {PLANOS.map(p => (
            <div
              key={p.nome}
              style={{
                flex: `0 0 ${mobile ? 220 : 240}px`,
                scrollSnapAlign: 'start',
                background: C.white,
                borderRadius: 16,
                padding: '24px 20px',
                border: p.destaque ? `2px solid ${p.cor}` : '1px solid #e5e9f0',
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {p.destaque && (
                <Chip
                  label="Mais completo"
                  size="small"
                  style={{
                    position: 'absolute',
                    top: -12,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    background: p.cor,
                    color: '#fff',
                    fontWeight: 700,
                    fontSize: 10.5,
                  }}
                />
              )}
              <p
                style={{
                  fontWeight: 700,
                  fontSize: 15,
                  color: p.cor,
                  margin: p.destaque ? '6px 0 0' : 0,
                }}
              >
                {p.nome}
              </p>
              <p
                style={{
                  fontWeight: 800,
                  fontSize: 24,
                  color: C.text,
                  margin: '8px 0 2px',
                }}
              >
                {p.preco}
                <span style={{fontSize: 12, fontWeight: 400, color: C.muted}}>
                  /mês
                </span>
              </p>
              <p style={{fontSize: 11.5, color: C.muted, marginBottom: 16}}>
                até {p.membros} pessoa{p.membros > 1 ? 's' : ''}
              </p>
              <div style={{flex: 1, marginBottom: 18}}>
                {p.beneficios.map(item => {
                  const texto = typeof item === 'string' ? item : item.texto;
                  const ativo = typeof item === 'string' ? true : item.ativo;
                  return (
                    <div
                      key={texto}
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 7,
                        padding: '3px 0',
                      }}
                    >
                      {ativo ? (
                        <CheckCircleIcon
                          style={{
                            fontSize: 15,
                            color: p.cor,
                            marginTop: 1.5,
                            flexShrink: 0,
                          }}
                        />
                      ) : (
                        <CancelOutlinedIcon
                          style={{
                            fontSize: 15,
                            color: '#d1d5db',
                            marginTop: 1.5,
                            flexShrink: 0,
                          }}
                        />
                      )}
                      <span
                        style={{
                          fontSize: 12,
                          color: ativo ? C.text : C.muted,
                          lineHeight: 1.4,
                          textDecoration: ativo ? 'none' : 'line-through',
                        }}
                      >
                        {texto}
                      </span>
                    </div>
                  );
                })}
              </div>
              {p.cta === 'Plano gratuito' ? (
                <p
                  style={{
                    textAlign: 'center',
                    fontSize: 12.5,
                    fontWeight: 700,
                    color: C.muted,
                    margin: 0,
                    padding: '10px 0',
                  }}
                >
                  Plano gratuito
                </p>
              ) : p.destaque ? (
                // Prime pausado: Integração OpenFinance (o diferencial deste
                // tier, ver openFinanceAtivo acima) exige custo fixo mensal
                // (Pluggy) que só compensa com uma base de assinantes maior —
                // ver RUNBOOK_LIBERAR_OPENFINANCE.md (repo microservices,
                // raiz) pro critério de quando reabrir.
                <>
                  <Button
                    fullWidth
                    disabled
                    variant="outlined"
                    startIcon={<LockOutlinedIcon style={{fontSize: 16}} />}
                    style={{
                      textTransform: 'none',
                      fontWeight: 700,
                      fontSize: 13.5,
                      borderRadius: 10,
                      padding: '9px 0',
                    }}
                  >
                    Em breve
                  </Button>
                  <p
                    style={{
                      fontSize: 10.5,
                      color: C.muted,
                      textAlign: 'center',
                      margin: '8px 0 0',
                      lineHeight: 1.35,
                    }}
                  >
                    Liberamos assim que tivermos assinantes suficientes pra
                    cobrir o custo de conectar direto com os bancos.
                  </p>
                </>
              ) : (
                <Button
                  component={Link}
                  to="/cadastro_financas"
                  fullWidth
                  variant={p.destaque ? 'contained' : 'outlined'}
                  style={{
                    textTransform: 'none',
                    fontWeight: 700,
                    fontSize: 13.5,
                    borderRadius: 10,
                    padding: '9px 0',
                  }}
                  sx={
                    p.destaque
                      ? {
                          bgcolor: p.cor,
                          ':hover': {bgcolor: p.cor, opacity: 0.9},
                        }
                      : {color: p.cor, borderColor: p.cor}
                  }
                >
                  Assinar
                </Button>
              )}
            </div>
          ))}
        </div>
        <p
          style={{
            textAlign: 'center',
            fontSize: 12,
            color: C.muted,
            marginTop: 24,
          }}
        >
          Comece pelo Free — sem cartão de crédito — e mude de plano quando
          quiser.
        </p>
      </Section>

      {/* ─── CTA final ───────────────────────────────────────────────────── */}
      <Section
        bg={`linear-gradient(135deg, ${C.laranja} 0%, ${C.laranjaD} 100%)`}
      >
        <div style={{textAlign: 'center', maxWidth: 620, margin: '0 auto'}}>
          <FavoriteIcon
            style={{color: '#fff', fontSize: 34, marginBottom: 16}}
          />
          <h2
            style={{
              color: '#fff',
              fontSize: 28,
              fontWeight: 800,
              margin: '0 0 14px',
            }}
          >
            Organizar as finanças não devia ser um privilégio de quem já entende
            de planilha.
          </h2>
          <p style={{color: '#ffe4cc', fontSize: 15, marginBottom: 28}}>
            Comece hoje — é o primeiro passo pra realizar os sonhos que vocês
            quiserem, um mês de cada vez.
          </p>
          <Button
            component={Link}
            to="/cadastro_financas"
            variant="contained"
            endIcon={<ArrowForwardIcon />}
            style={{
              textTransform: 'none',
              fontWeight: 700,
              fontSize: 16,
              padding: '14px 32px',
              borderRadius: 12,
            }}
            sx={{
              bgcolor: '#fff',
              color: C.laranjaD,
              ':hover': {bgcolor: '#fff2e8'},
            }}
          >
            Criar minha conta grátis
          </Button>
        </div>
      </Section>

      <Footer />
    </div>
  );
};

export default FinancasLanding;
